// src/components/Echart/EChartLive.jsx
// ============================================================
// Wrapper ECharts INTERACTIF : comme Echart, mais expose les événements
// (clic, survol…) via `onEvents` pour faire communiquer les graphes entre eux.
// Les handlers passent par une ref → toujours frais sans re-binder.
// ============================================================
import React, { useEffect, useRef } from "react";
import * as echarts from "echarts";
import "./Echart.scss";

function alive(c) {
  return c && !c.isDisposed();
}

export default function EChartLive({
  option,
  className = "",
  onEvents,
  onReady,
}) {
  const elRef = useRef(null);
  const chartRef = useRef(null);
  const rafRef = useRef(0);
  const evRef = useRef(onEvents);
  evRef.current = onEvents;

  useEffect(() => {
    const el = elRef.current;
    if (!el) return undefined;
    const chart = echarts.init(el, null, { renderer: "canvas" });
    chartRef.current = chart;

    const fit = () => {
      if (!alive(chartRef.current)) return;
      try {
        chartRef.current.resize();
      } catch (err) {
        /* noop */
      }
    };
    const ro = new ResizeObserver(() => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      rafRef.current = requestAnimationFrame(fit);
    });
    ro.observe(el);
    window.addEventListener("resize", fit);
    fit();

    // Liaison des événements une seule fois ; dispatch via la ref.
    const names = evRef.current ? Object.keys(evRef.current) : [];
    const handlers = names.map((name) => {
      const h = (params) => {
        const fn = evRef.current && evRef.current[name];
        if (typeof fn === "function") fn(params);
      };
      chart.on(name, h);
      return h;
    });

    if (typeof onReady === "function") onReady(chart);

    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      window.removeEventListener("resize", fit);
      ro.disconnect();
      names.forEach((name, i) => {
        if (alive(chart)) chart.off(name, handlers[i]);
      });
      // `clear()` AVANT `dispose()`. ECharts programme ses propres frames
      // d'animation ; si l'instance est détruite alors qu'un tracé est
      // encore en file, son moteur de rendu déréférence un layerStack déjà
      // nul et lève « Cannot read properties of null (reading 'layerStack') ».
      // Le cas se produit quand on change d'onglet pendant l'animation
      // d'entrée — et il est systématique en développement, où React monte,
      // démonte puis remonte chaque effet. `clear()` vide les composants et
      // arrête les animations en cours, ce qui rend la destruction sûre.
      if (alive(chart)) {
        try {
          chart.clear();
        } catch (err) {
          /* noop */
        }
        chart.dispose();
      }
      chartRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const chart = chartRef.current;
    if (!alive(chart) || !option) return;
    try {
      chart.setOption(option, true);
    } catch (err) {
      /* noop */
    }
  }, [option]);

  return <div ref={elRef} className={`echart ${className}`} />;
}
